import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';
import { companyService } from '@/services/company.service';
import { tenantService } from '@/services/tenant.service';

interface Props {
  children: ReactNode;
}

/**
 * BrandingProvider loads the appropriate branding config for the
 * authenticated user's tenant/company and injects CSS custom properties
 * so the entire UI reflects the correct brand.
 */
export function BrandingProvider({ children }: Props) {
  const appUser = useAuthStore((s) => s.appUser);
  const { setBranding, resetBranding } = useBrandingStore();

  useEffect(() => {
    if (!appUser) {
      resetBranding();
      return;
    }

    async function loadBranding() {
      if (!appUser) return;

      // Company-level branding takes priority
      if (appUser.tenantId && appUser.companyId) {
        const company = await companyService.getById(appUser.tenantId, appUser.companyId).catch(() => null);
        if (company?.branding) {
          setBranding({
            companyName: company.branding.companyName || company.name,
            logoUrl: company.branding.logoUrl,
            primaryColor: company.branding.primaryColor,
            secondaryColor: company.branding.secondaryColor,
            tenantId: appUser.tenantId,
            companyId: appUser.companyId,
          });
          applyColors(company.branding.primaryColor, company.branding.secondaryColor);
          return;
        }
      }

      // Fallback to tenant branding
      if (appUser.tenantId) {
        const tenant = await tenantService.getById(appUser.tenantId).catch(() => null);
        if (tenant?.branding) {
          setBranding({
            companyName: tenant.branding.companyName || tenant.name,
            logoUrl: tenant.branding.logoUrl,
            primaryColor: tenant.branding.primaryColor,
            secondaryColor: tenant.branding.secondaryColor,
            tenantId: appUser.tenantId,
          });
          applyColors(tenant.branding.primaryColor, tenant.branding.secondaryColor);
          return;
        }
      }

      // Default: Arlo Auto platform branding
      resetBranding();
      applyColors('#1E3A5F', '#F59E0B');
    }

    loadBranding();
  }, [appUser, setBranding, resetBranding]);

  return <>{children}</>;
}

function applyColors(primary: string, secondary: string) {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-secondary', secondary);

  // Convert hex to HSL for Tailwind CSS variables
  const [ph, ps, pl] = hexToHsl(primary);
  const [sh, ss, sl] = hexToHsl(secondary);
  root.style.setProperty('--primary', `${ph} ${ps}% ${pl}%`);
  root.style.setProperty('--secondary', `${sh} ${ss}% ${sl}%`);
}

function hexToHsl(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [215, 54, 24];

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}
